import React from 'react';
import MuiContainer, { ContainerProps } from '@mui/material/Container';
import LogoDark from '../../../assets/Dx by Dewise - dark.svg';
import LogoLight from '../../../assets/Dx by Dewise - light.svg';
import { styledComponent } from '../../../hoc';
import { Card } from '../../card';
import { Grid } from '../../grid';
import { Typography } from '../../typography';
import MuiCardHeader from '@mui/material/CardHeader';
import MuiCardContent from '@mui/material/CardContent';
import { useTheme } from '../../../theme/ThemeProvider';
import Box from '@mui/material/Box';
/**
 * Represents the properties for the `PageComponent`.
 */
export type PageComponentProps = {
  /**
   * Optional component to render as the page header.
   */
  HeaderComponent?: React.ElementType;

  /**
   * Optional component to render as the page footer.
   */
  FooterComponent?: React.ReactNode;

  /**
   * The main content area of the page.
   */
  children: React.ReactNode;

  /**
   * The variant of page layout to render.
   */
  variant: 'card' | 'container';

  /**
   * Flag to determine if the page has an action menu.
   */
  hasActionMenu?: boolean;

  /**
   * Optional disables the default color of text of buttons inHeaderComponent
   */
  disableDefaultHeaderBtnColor?: boolean;

  /**
   * Flag to determine if a backdrop should be shown.
   */
  showBackdrop?: boolean;
} & HeaderOwnProps;

/**
 * Represents the properties for the `Header` component.
 */
export type HeaderOwnProps = {
  variant: 'card' | 'container';
  hasActionMenu?: boolean;
  disableDefaultHeaderBtnColor?: boolean;
  hideFooter?: boolean;
  disableGutters?: boolean;
};

const HeaderContainer = styledComponent<
  React.HTMLAttributes<HTMLDivElement> & HeaderOwnProps
>('div', {
  ignoreProps: ['variant', 'hasActionMenu', 'disableDefaultHeaderBtnColor'],
})(({ theme, ...props }) => ({
  padding: '16px 32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
  width: '100%',
  color: `${theme.palette.surface.onSurfaceVar}!important`,

  ...(props.variant === 'card' && {
    backgroundColor: `${theme.palette.surfaceContainer.low} !important`,
    color: theme.palette.primary.main,

    ...(!props.disableDefaultHeaderBtnColor && {
      '& button': {
        color: `${theme.palette.surface.onSurfaceVar} !important`,
      },
    }),

    ...(props.hasActionMenu && {
      padding: '16px 16px 16px 32px',

      '& button': {
        color: `${theme.palette.surface.onSurfaceVar} !important`,
      },
    }),
  }),
}));

/**
 * `FullPage` Component
 *
 * A layout component that provides a full-page structure with a card wrapping the main content
 * and a footer containing company information and navigation links. This is typically used as a
 * base for pages that require a standard page layout with a header, content area, and footer.
 *
 * @remarks
 * The component consists of two main sections:
 * - **Content Section**: The children of the `FullPage` component are wrapped inside a `Card` element.
 * - **Footer Section**: Contains the company Dark, product description, navigation links, and copyright notice.
 *
 * @param {PageComponentProps} props - The props to configure the component, including the `children` for the main content.
 *
 * @returns {JSX.Element} The full-page layout with the content area and footer.
 *
 * @example
 * ```tsx
 * import FullPage from './FullPage';
 *
 * const App = () => {
 *   return (
 *     <FullPage>
 *       <div>Your content goes here</div>
 *     </FullPage>
 *   );
 * };
 *
 * export default App;
 * ```
 *
 * @category Layout
 */
export const FullPage = (props: PageComponentProps): JSX.Element => {
  const Body = props.variant === 'container' ? ContainerBody : CardContainer;
  return (
    <Container maxWidth={false}>
      <Content>
        {/* Main content wrapped inside a Card */}
        <Body {...props} />
        {/* Footer section */}
        {!props.hideFooter && <PageFooter />}
      </Content>
    </Container>
  );
};

const CardContainer = (props: PageComponentProps) => {
  return (
    <Card sx={{ position: 'relative' }}>
      <HeaderContainer
        variant={props.variant}
        hasActionMenu={props.hasActionMenu}
        disableDefaultHeaderBtnColor={props.disableDefaultHeaderBtnColor}
      >
        {props.HeaderComponent && (
          <MuiCardHeader component={props.HeaderComponent} />
        )}
      </HeaderContainer>
      <MuiCardContent
        sx={
          props.variant === 'card'
            ? {
                background: theme => theme.palette.surfaceContainer.lowest,
                padding: props.disableGutters ? 'unset' : '16px 32px 32px 32px',
              }
            : undefined
        }
      >
        {props.children}
      </MuiCardContent>
      {props.showBackdrop && (
        <Box
          component="div"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#D0BCFF',
            zIndex: 1300,
            opacity: 0.16,
          }}
        />
      )}
    </Card>
  );
};

const ContainerBody = (props: PageComponentProps) => {
  return <Container sx={{ my: 4 }}>{props.children}</Container>;
};

/**
 * `PageFooter` Component
 *
 * A footer section containing company information and navigation links. The footer is rendered at
 * the bottom of the page, and includes a company logo, a product description, navigational links, and
 * a copyright notice.
 *
 * @returns {JSX.Element} The footer layout with company information and links.
 *
 * @example
 * ```tsx
 * <PageFooter />
 * ```
 */
const PageFooter = (): JSX.Element => {
  const { theme } = useTheme();

  const logo = theme === 'dark' ? LogoDark : LogoLight;

  return (
    <Grid container columnSpacing={3} sx={{ pb: '32px', mt: '64px' }}>
      {/* Company logo */}
      <Grid size={{ xs: 12, sm: 12 }}>
        <img src={logo} alt="logo" />
      </Grid>
      {/* Product description */}
      <Grid size={{ xs: 12, sm: 5 }}>
        <Typography fontSize={12}>
          DevEx by Dewise is a customized adaptation of Spotify’s Backstage.
          Engineered to optimize our internal software development lifecycle, it
          provides a space for plugins development that automates mundane tasks,
          enhances productivity, consolidates technical documentation across
          different products, and essentially makes each Dewise engineer's life
          lighter.
        </Typography>
      </Grid>

      {/* Copyright notice */}
      <Grid
        size={{ xs: 12, sm: 12 }}
        sx={{ alignSelf: 'flex-end', textAlign: 'end' }}
      >
        <Typography fontSize={12}>© 2025 Dewise ApS</Typography>
      </Grid>
    </Grid>
  );
};

const Container = styledComponent<ContainerProps>(MuiContainer)(() => ({
  minHeight: 'calc(100vh - 85px)',
}));

const Content = styledComponent('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 'inherit',
}));

export default FullPage;
